import React from "react";
import locale from "@bonliva-crm/locale";
import { useObserver } from "mobx-react";
import { groupBy } from "lodash";
import DeleteIcon from "@material-ui/icons/Clear";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { ThemeType } from "@theme";
import { Body } from "@components";
import { Checkbox, Text } from "@components/formx";
import IconButton from "@material-ui/core/IconButton";
import { OnlyExperienceStore, Specialization } from "./OnlyExperienceStore";
import { ExpTopPanel, ExpMultiSelect, ExpKnownSystem, OutlinedInput } from "../Common";
import { useConsultantEditorContext } from "../../ConsultantEditorContext";
import Select, { Option } from "@common/Select";
import { parseSpec } from "../../../utils";

type Props = {
  store: OnlyExperienceStore;
};

export function PsykologExpertise(props: Props) {
  const { store } = props;

  const editorStore = useConsultantEditorContext();

  const profItem = `professions.${store.storeIndex}`;
  // the "specializations" error is either a message for the whole array or an
  // array of per-item errors; only the former should be rendered here
  const specializationsError = editorStore.form.getError(`${profItem}.specializations`);
  const errorMsg = !Array.isArray(specializationsError)
    ? specializationsError
    : undefined;
  const classes = useStyles({});

  const specOptions: Option[] = [];
  const groupedSpecs = groupBy(store.selectSpecializationList.map(parseSpec), "parent");

  for (const group in groupedSpecs) {
    const children = groupedSpecs[group].map((x) => ({
      label: x.label,
      value: x.value,
      parent: group
    }));

    specOptions.push({
      label: group,
      value: group,
      children: children
    });

    specOptions.push(...children);
  }

  const methodOptions: Option[] = [];
  const groupedMethods = groupBy(store.methods.map(parseSpec), "parent");

  for (const group in groupedMethods) {
    const children = groupedMethods[group].map((x) => ({
      label: x.label,
      value: x.value,
      parent: group
    }));

    methodOptions.push({
      label: group,
      value: group,
      children: children
    });

    methodOptions.push(...children);
  }

  return useObserver(() => (
    <>
      <Paper className={classes.info}>
        <ExpTopPanel
          certifiedPath={`${profItem}.certifiedFrom`}
          certifiedFromTxt="Psykolog sedan"
          onDeleteClick={store.deleteExpertise}
        />

        {errorMsg && (
          <Body color="error" align="center">
            {errorMsg}
          </Body>
        )}

        <ExpMultiSelect
          name={locale.AddExp}
          selectOptions={specOptions}
          onChange={(val) => store.setSelectedSpecialization(val)}
          selectedVal={store.selectedSpecialization}
          btnClick={() => store.addSpec()}
          btnDisabled={!store.selectedSpecialization}
          SelectProps={{
            groupBy: "parent",
            hideGroupCheckbox: true
          }}
        />
      </Paper>

      {store.specializations.length !== 0 && (
        <ExperienceTable
          name={locale.Experienced}
          profItem={`${profItem}.specializations`}
          items={store.specializations}
          deleteRow={store.removeSpec}
          profession={store.profession}
        />
      )}

      <Paper style={{ padding: 16 }}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <ExpKnownSystem
              options={store.systems}
              onChange={store.addSystems}
              value={store.selectedSystems}
              hasValidated={true}
              validate={(e, c) => {
                if (c) {
                  editorStore.consultantCVManager.approveKey(
                    `professions.${store.storeIndex}.systems`
                  );
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Select
              label={locale.Methods}
              fullWidth
              multi
              options={methodOptions}
              sortOptions={true}
              optionType={"checkbox"}
              onChange={store.addMethods}
              value={store.selectedMethods}
              groupBy={"parent"}
              hideGroupCheckbox={true}
              multiType={"chips"}
            />
          </Grid>
        </Grid>
      </Paper>
    </>
  ));
}

type ExperienceTableProps = {
  name: string;
  profItem: string;
  profession: string;
  items: Specialization[];
  deleteRow: (path: string, i: number) => void;
};

function ExperienceTable(props: ExperienceTableProps) {
  const { items, profItem, deleteRow, profession } = props;

  // preserve the original indexes of elements in the array
  const itemNames = items
    .map((x) => x.name)
    .map(parseSpec)
    .map((x, i) => ({ spec: x, index: i }));
  const groups = itemNames
    .map((x) => x.spec.parent)
    .sortBy((x) => x)
    .unique();

  function itemsInGroup(group: string) {
    return itemNames.filter((x) => x.spec.parent === group);
  }

  return useObserver(() => (
    <Paper>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell style={{ width: "25%" }}>{locale.Specialization}</TableCell>
            <TableCell align="center" style={{ width: 64 }}>
              {locale.Experienced}
            </TableCell>
            <TableCell align="center" style={{ width: "15rem" }}>
              {locale.ExperienceYearFrom}
            </TableCell>
            <TableCell align="center" style={{ width: 64, whiteSpace: "nowrap" }}>
              {locale.Interested}
            </TableCell>
            <TableCell align="center">{locale.Comment}</TableCell>
            <TableCell style={{ width: 48 }} />
          </TableRow>
        </TableHead>

        <TableBody>
          {groups.map((group) => (
            <React.Fragment key={group}>
              {group && (
                <TableRow style={{ background: "aliceblue" }}>
                  <TableCell colSpan={5} style={{ fontSize: "1.1rem", fontWeight: 500 }}>
                    {group}
                  </TableCell>
                </TableRow>
              )}
              {itemsInGroup(group).map((item, i) => {
                const itemName = `${profItem}.${item.index}`;

                return (
                  <TableRow key={itemName}>
                    <TableCell style={{ fontWeight: "bold" }}>
                      {item.spec.label}
                    </TableCell>

                    <TableCell align="center">
                      <Checkbox name={`${itemName}.qualified`} />
                    </TableCell>

                    <TableCell align="center">
                      <OutlinedInput
                        name={`${itemName}.yearFrom`}
                        label={locale.ExperienceYearFrom}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Checkbox name={`${itemName}.interested`} />
                    </TableCell>

                    <TableCell align="center">
                      <Text name={`${itemName}.comment`} label={locale.WriteComment} />
                    </TableCell>

                    <TableCell align="center">
                      <IconButton
                        onClick={() => deleteRow(profItem, item.index)}
                        disabled={item.spec.label === profession}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </Paper>
  ));
}

const useStyles = makeStyles((theme: ThemeType) => ({
  info: {
    ...theme.mixins.childrenMarginBottom(8),
    position: "relative",
    padding: 16
  }
}));
